import type { ObjectSchema } from "yup";

import {
  mineSubsidenceScheduledValidation,
  personalPropertyOptions,
  scheduledPersonalPropertyValidation,
} from "@covertree/new-business-forms";
import type { ExtraCoverageKeys } from "@covertree/new-business-graphql";
import type { OptionType } from "@covertree/types";

/** One structured sub-item of a coverage (e.g. a scheduled other structure). */
export type ExtraCoverageItem = Record<string, string | number | null>;

/** One field of the inline add/edit form of a field-group peril. */
export interface ExtraCoverageItemField {
  name: string;
  label: string;
  type: "text" | "currency" | "select";
  /** Choices for a `select` field. */
  options?: readonly OptionType<string>[];
}

/** The only perils whose value is a list of field-group items. */
export type FieldGroupPeril = Extract<
  ExtraCoverageKeys,
  | "mineSubsidenceScheduled"
  | "scheduledPersonalProperty"
  | "secondaryResidenceLiability"
>;

export interface FieldGroup {
  fields: readonly ExtraCoverageItemField[];
  validation?: ObjectSchema<ExtraCoverageItem>;
  /**
   * Backend id key. Pending unification in CTD2C-7568
   */
  locatorKey: "locator" | "fieldGroupLocator";
}

/**
 * Field groups per peril. Fields and validation mirror the new-business flow.
 */
export const FIELD_GROUP_PERILS: Record<FieldGroupPeril, FieldGroup> = {
  mineSubsidenceScheduled: {
    locatorKey: "locator",
    fields: [
      { name: "description", label: "Structure description", type: "text" },
      { name: "value", label: "Value", type: "currency" },
    ],
    validation:
      mineSubsidenceScheduledValidation as unknown as ObjectSchema<ExtraCoverageItem>,
  },
  scheduledPersonalProperty: {
    locatorKey: "fieldGroupLocator",
    fields: [
      {
        name: "type",
        label: "Item type",
        type: "select",
        options: personalPropertyOptions,
      },
      { name: "description", label: "Item description", type: "text" },
      { name: "value", label: "Value", type: "currency" },
    ],
    validation:
      scheduledPersonalPropertyValidation as unknown as ObjectSchema<ExtraCoverageItem>,
  },
  secondaryResidenceLiability: {
    locatorKey: "fieldGroupLocator",
    fields: [{ name: "address", label: "Address", type: "text" }],
  },
};

export const isFieldGroupPeril = (key: string): key is FieldGroupPeril =>
  key in FIELD_GROUP_PERILS;

/** Keys accepted by the backend for each field-group item. */
export const fieldGroupItemKeys = (
  peril: FieldGroupPeril
): readonly string[] => {
  const { locatorKey, fields } = FIELD_GROUP_PERILS[peril];

  return [locatorKey, ...fields.map(({ name }) => name)];
};
